import React, { FC, useEffect } from "react";
import {
  BranchType,
  ResponseBranch,
  ResponseBranchNodeData,
  ResponseMatchType,
  ResponseRoutingMode,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import { v4 as uuid } from "uuid";
import Button, { ButtonType } from "components/Elements/Buttonv2/Button";
import TimeDelayEditor from "../components/TimeDelayEditor";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";

const matchTypeOptions: { key: ResponseMatchType; label: string }[] = [
  { key: "keyword", label: "Keyword" },
  { key: "contains", label: "Contains" },
  { key: "regex", label: "Regex" },
];

const ResponseBranchSettings: FC<
  SidePanelComponentProps<ResponseBranchNodeData>
> = ({ nodeData, setNodeData, setIsError, showErrors }) => {
  const readOnly = useSidePanelReadOnly();
  const branches = nodeData.branches || [];
  // Match the server matcher: a node with no explicit routingMode is
  // treated as keyword (the legacy behavior). New nodes are assigned
  // routingMode: "ai" by the reducer when they're created, so opening
  // a brand-new node still shows the AI panel by default.
  const routingMode: ResponseRoutingMode =
    nodeData.routingMode === "ai" ? "ai" : "keyword";

  const matchBranches = branches.filter((b) => !b.isOthers && !b.isTimeout);
  const othersBranch = branches.find((b) => b.isOthers);
  const timeoutBranch = branches.find((b) => b.isTimeout);

  const hasAnyMatch =
    routingMode === "ai"
      ? matchBranches.some(
          (b) => (b.label && b.label.trim()) || (b.prompt && b.prompt.trim())
        )
      : matchBranches.some(
          (b) =>
            (b.label && b.label.trim()) || (b.matchValue && b.matchValue.trim())
        );

  // Validate any regex matchValue at edit time (keyword mode only). The
  // server's matcher returns false on a malformed regex, which silently
  // breaks routing.
  const regexErrorFor = (b: ResponseBranch): string | null => {
    if (routingMode !== "keyword") return null;
    if (b.matchType !== "regex") return null;
    if (!b.matchValue) return null;
    try {
      new RegExp(b.matchValue);
      return null;
    } catch (e: any) {
      return e?.message || "Invalid regular expression";
    }
  };
  const hasInvalidRegex = matchBranches.some((b) => regexErrorFor(b) !== null);
  const isInvalid =
    matchBranches.length === 0 || !hasAnyMatch || hasInvalidRegex;

  useEffect(() => {
    setIsError(isInvalid);
  }, [isInvalid, setIsError]);

  const update = (next: ResponseBranch[]) =>
    setNodeData({ ...nodeData, branches: next });

  const setRoutingMode = (mode: ResponseRoutingMode) =>
    setNodeData({ ...nodeData, routingMode: mode });

  const addMatchBranch = () => {
    const newBranch: ResponseBranch = {
      id: uuid(),
      type: BranchType.RESPONSE,
      label: "",
      matchType: "keyword",
      matchValue: "",
      prompt: "",
    };
    // Insert before the isOthers / isTimeout tail so order stays sensible.
    const tail = branches.filter((b) => b.isOthers || b.isTimeout);
    const head = branches.filter((b) => !b.isOthers && !b.isTimeout);
    update([...head, newBranch, ...tail]);
  };

  const addTimeoutBranch = () => {
    if (timeoutBranch) return;
    const newBranch: ResponseBranch = {
      id: uuid(),
      type: BranchType.RESPONSE,
      label: "No reply within",
      matchType: "keyword",
      matchValue: "",
      isTimeout: true,
      delay: { days: 1, hours: 0, minutes: 0 },
    };
    // Place before isOthers (which always lives at the end).
    const others = branches.filter((b) => b.isOthers);
    const rest = branches.filter((b) => !b.isOthers);
    update([...rest, newBranch, ...others]);
  };

  const removeBranch = (id: string) =>
    update(branches.filter((b) => b.id !== id));

  const patchBranch = (id: string, patch: Partial<ResponseBranch>) =>
    update(branches.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const modeButton = (mode: ResponseRoutingMode, label: string) => (
    <button
      type="button"
      onClick={() => setRoutingMode(mode)}
      className={`flex-1 px-3 py-2 text-[13px] font-medium border rounded ${
        routingMode === mode
          ? "bg-[#7C3AED] text-white border-[#7C3AED]"
          : "bg-themed-card text-themed-text border-themed-borderSecondary"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className={`p-5 flex flex-col gap-4${
        readOnly ? " pointer-events-none select-none" : ""
      }`}
    >
      {/* Routing mode selector */}
      <div>
        <div className="text-[14px] font-semibold mb-2">Routing mode</div>
        <div className="flex gap-2">
          {modeButton("ai", "AI")}
          {modeButton("keyword", "Keyword")}
        </div>
        <div className="text-[11px] text-themed-textSecondary mt-2">
          {routingMode === "ai"
            ? "Each branch describes (in plain English) the kind of reply that belongs there. The AI reads the contact's reply and picks the matching branch."
            : "Each branch matches against keywords, a substring, or a regex. Optionally let the AI pick a branch by label when no rule matches."}
        </div>
      </div>

      {/* Match branches */}
      {matchBranches.map((b, i) => (
        <div
          key={b.id}
          className="border border-themed-border rounded p-3 flex flex-col gap-2"
        >
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-semibold">Branch {i + 1}</div>
            <Button
              type={ButtonType.LINK}
              onClick={() => removeBranch(b.id)}
              className="!text-[#EB5757]"
            >
              Delete
            </Button>
          </div>

          <div>
            <div className="text-[12px] font-medium mb-1">Label</div>
            <input
              type="text"
              value={b.label || ""}
              placeholder="e.g. interested / not interested / book a demo"
              onChange={(e) => patchBranch(b.id, { label: e.target.value })}
              className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] bg-themed-card focus:outline-none focus:border-[#7C3AED]"
            />
            <div className="text-[11px] text-themed-textSecondary mt-1">
              Shown on the branch in the canvas.
            </div>
          </div>

          {routingMode === "ai" ? (
            <div>
              <div className="text-[12px] font-medium mb-1">
                What does a reply on this branch look like?
              </div>
              <textarea
                rows={8}
                value={b.prompt || ""}
                placeholder="e.g. contact says they want to book a call, schedule a demo, or pick a time"
                onChange={(e) => patchBranch(b.id, { prompt: e.target.value })}
                className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] bg-themed-card focus:outline-none focus:border-[#7C3AED]"
              />
              <div className="text-[11px] text-themed-textSecondary mt-1">
                The AI uses this to decide whether the contact's reply belongs
                on this branch.
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[12px] font-medium mb-1">Match type</div>
                  <select
                    value={b.matchType}
                    onChange={(e) =>
                      patchBranch(b.id, {
                        matchType: e.target.value as ResponseMatchType,
                      })
                    }
                    className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] bg-themed-card focus:outline-none focus:border-[#7C3AED]"
                  >
                    {matchTypeOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-[12px] font-medium mb-1">
                    Case sensitive
                  </div>
                  <label className="inline-flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={!!b.caseSensitive}
                      onChange={(e) =>
                        patchBranch(b.id, { caseSensitive: e.target.checked })
                      }
                    />
                    <span className="text-[12px]">match case</span>
                  </label>
                </div>
              </div>

              <div>
                <div className="text-[12px] font-medium mb-1">Match value</div>
                <input
                  type="text"
                  value={b.matchValue || ""}
                  placeholder={
                    b.matchType === "regex"
                      ? "e.g. ^(yes|yeah|yep)\\b"
                      : b.matchType === "contains"
                      ? "any substring of the reply"
                      : "single word, e.g. YES"
                  }
                  onChange={(e) =>
                    patchBranch(b.id, { matchValue: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded text-[13px] bg-themed-card focus:outline-none focus:border-[#7C3AED] ${
                    regexErrorFor(b)
                      ? "border-[#E11D48]"
                      : "border-themed-borderSecondary"
                  }`}
                />
                {regexErrorFor(b) && (
                  <div className="text-[11px] text-[#E11D48] mt-1">
                    {regexErrorFor(b)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ))}

      <Button type={ButtonType.SECONDARY} onClick={addMatchBranch}>
        Add reply branch
      </Button>

      {/* Timeout branch */}
      <div className="border-t border-themed-border pt-4">
        <div className="text-[14px] font-semibold mb-2">No-reply timeout</div>
        {timeoutBranch ? (
          <div className="border border-themed-border rounded p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-[13px]">Contact hasn't replied within…</div>
              <Button
                type={ButtonType.LINK}
                onClick={() => removeBranch(timeoutBranch.id)}
                className="!text-[#EB5757]"
              >
                Remove
              </Button>
            </div>
            <TimeDelayEditor
              delay={
                timeoutBranch.delay ?? {
                  days: 0,
                  hours: 0,
                  minutes: 0,
                }
              }
              onChange={(delay) => patchBranch(timeoutBranch.id, { delay })}
            />
          </div>
        ) : (
          <Button type={ButtonType.SECONDARY} onClick={addTimeoutBranch}>
            Add no-reply timeout branch
          </Button>
        )}
      </div>

      {/* AI fallback toggle — only useful in keyword mode. In AI mode the
          AI is already the primary router, so the toggle would be
          redundant. */}
      {routingMode === "keyword" && (
        <div className="border-t border-themed-border pt-4">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={!!nodeData.aiFallback}
              onChange={(e) =>
                setNodeData({ ...nodeData, aiFallback: e.target.checked })
              }
              className="mt-1"
            />
            <div>
              <div className="text-[14px] font-semibold">AI fallback</div>
              <div className="text-[12px] text-themed-textSecondary">
                When a reply doesn't match any branch keyword, ask the AI to
                pick the best-fitting branch label. If the AI also can't pick
                one, the "Any other reply" branch is used.
              </div>
            </div>
          </label>
        </div>
      )}

      {/* All others (read-only marker) */}
      <div className="border-t border-themed-border pt-4">
        <div className="text-[14px] font-semibold mb-1">Any other reply</div>
        <div className="text-[12px] text-themed-textSecondary">
          A catch-all branch is always present. Anything that doesn't match a
          rule above (and isn't classified by the AI) routes there.
          {!othersBranch && (
            <span className="block mt-1 text-[#F43F5E]">
              The catch-all branch will be created when you save.
            </span>
          )}
        </div>
      </div>

      {showErrors && isInvalid && (
        <div className="text-[12px] text-[#F43F5E]">
          {routingMode === "ai"
            ? "Add at least one reply branch with a label or prompt before saving."
            : "Add at least one reply branch with a label or match value before saving."}
        </div>
      )}
    </div>
  );
};

export default ResponseBranchSettings;
